/**
 * Create FoodCard Modal - Clean, minimal design
 * Snap/upload photo → AI generates card → Post to feed or save
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { 
  X, Camera, Upload, Loader, Sparkles, MapPin, Hash, 
  Check, Bookmark, ArrowRight, Image as ImageIcon
} from 'lucide-react';
import { useLocation } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreateFoodCardModal = ({ isOpen, onClose, onCardCreated }) => {
  const { user } = useAuth();
  const { locationString } = useLocation();
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Stage: 'capture' | 'analyzing' | 'preview' | 'publishing'
  const [stage, setStage] = useState('capture');
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  
  // AI Analysis result
  const [cardData, setCardData] = useState(null);
  
  // Editable fields
  const [dishName, setDishName] = useState('');
  const [description, setDescription] = useState('');
  const [cuisine, setCuisine] = useState('');
  const [venueName, setVenueName] = useState('');
  const [hashtags, setHashtags] = useState([]);
  const [caption, setCaption] = useState('');
  const [newTag, setNewTag] = useState('');

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetModal();
      stopCamera();
    }
  }, [isOpen]);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => stopCamera();
  }, []);

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const startCamera = async () => {
    // iOS PWA/Safari may block getUserMedia — fall back to native file picker with capture
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS || !navigator.mediaDevices?.getUserMedia) {
      // Trigger native camera via file input with capture attribute
      if (fileInputRef.current) {
        fileInputRef.current.setAttribute('capture', 'environment');
        fileInputRef.current.click();
        // Remove capture after click so "From Gallery" still works without camera
        setTimeout(() => fileInputRef.current?.removeAttribute('capture'), 500);
      }
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      setCameraStream(stream);
      setShowCamera(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (err) {
      // getUserMedia failed — fall back to native file input with capture
      if (fileInputRef.current) {
        fileInputRef.current.setAttribute('capture', 'environment');
        fileInputRef.current.click();
        setTimeout(() => fileInputRef.current?.removeAttribute('capture'), 500);
      }
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setImagePreview(dataUrl);
    
    canvas.toBlob((blob) => {
      const file = new File([blob], 'capture.jpg', { type: 'image/jpeg' });
      setImageFile(file);
    }, 'image/jpeg', 0.9);
    
    stopCamera();
  };

  const resetModal = () => {
    setStage('capture');
    setImagePreview(null);
    setImageFile(null);
    setUploadedImageUrl(null);
    setCardData(null);
    setDishName('');
    setDescription('');
    setCuisine('');
    setVenueName('');
    setHashtags([]);
    setCaption('');
    setNewTag('');
  };

  const handleClose = () => {
    resetModal();
    stopCamera();
    onClose();
  };

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file type', description: 'Please select an image', variant: 'destructive' });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Max 10MB allowed', variant: 'destructive' });
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
  }, [toast]);

  const uploadImage = async () => {
    if (!imageFile) return null;
    const formData = new FormData();
    formData.append('file', imageFile);

    try {
      const res = await fetch(`${API_URL}/api/media/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      return data.url;
    } catch (e) {
      console.error('Image upload error:', e);
      return null;
    }
  };

  const handleAnalyze = async () => {
    if (!imagePreview) return;
    setStage('analyzing');

    try {
      // Upload image first
      let imageUrl = uploadedImageUrl;
      if (!imageUrl && imageFile) {
        imageUrl = await uploadImage();
        setUploadedImageUrl(imageUrl);
      }

      const base64Data = imagePreview.split(',')[1];

      // Use Gemini Vision for enhanced analysis
      const res = await fetch(`${API_URL}/api/foodcard/analyze-gemini`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          image_base64: base64Data,
          image_url: imageUrl,
          location: locationString,
          venue_name: venueName
        })
      });

      if (!res.ok) throw new Error('Analysis failed');
      const data = await res.json();

      if (data.error && !data.dish_name) {
        throw new Error(data.error);
      }

      setCardData(data);
      setDishName(data.dish_name || '');
      setDescription(data.description || '');
      setCuisine(data.cuisine || '');
      setHashtags(data.hashtags || ['FoodQuest']);
      setCaption(data.suggested_caption || '');
      setStage('preview');
    } catch (e) {
      console.error('AI analysis error:', e);
      toast({ title: 'Analysis failed', description: 'Could not identify the dish. Try a clearer photo.', variant: 'destructive' });
      setStage('capture');
    }
  };

  const handleAddHashtag = () => {
    if (newTag.trim() && !hashtags.includes(newTag.trim())) {
      setHashtags([...hashtags, newTag.trim().replace('#', '')]);
      setNewTag('');
    }
  };

  const handleRemoveHashtag = (tag) => {
    setHashtags(hashtags.filter(h => h !== tag));
  };

  const handlePublish = async (saveOnly = false) => {
    if (!user) {
      toast({ title: 'Please sign in', description: 'You need to be signed in to create FoodCards', variant: 'destructive' });
      return;
    }

    setStage('publishing');

    try {
      const formData = new FormData();
      formData.append('dish_name', dishName);
      formData.append('description', description);
      formData.append('cuisine', cuisine || cardData?.cuisine || 'Various');
      formData.append('venue_name', venueName);
      formData.append('location', locationString || '');
      formData.append('vibe', cardData?.vibe || 'Casual');
      formData.append('hashtags', hashtags.map(t => t.startsWith('#') ? t.slice(1) : t).join(','));
      formData.append('caption', caption);
      
      if (uploadedImageUrl) {
        formData.append('image_url', uploadedImageUrl);
      } else if (imagePreview) {
        formData.append('image_url', imagePreview);
      }

      if (saveOnly) {
        formData.append('save_only', 'true');
      }

      const res = await fetch(`${API_URL}/api/foodcard/create`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!res.ok) throw new Error('Failed to create');
      const data = await res.json();
      if (data.error) throw new Error(data.error);

      toast({ 
        title: saveOnly ? 'Saved to FoodList!' : 'FoodCard Posted!', 
        description: saveOnly ? 'Your FoodCard has been saved privately' : 'Your FoodCard is now live on the feed'
      });
      
      onCardCreated?.();
      handleClose();
    } catch (e) {
      console.error('Publish error:', e);
      toast({ title: 'Failed to publish', description: e.message, variant: 'destructive' });
      setStage('preview');
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[10000] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div 
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="create-foodcard-modal"
      >
        {/* Header - Clean, minimal */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-[#F4C430] to-[#FF6B35] rounded-xl flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Create FoodCard</h2>
              <p className="text-xs text-gray-500">
                {stage === 'capture' && 'Snap your dish'}
                {stage === 'analyzing' && 'AI is analyzing...'}
                {stage === 'preview' && 'Review & post'}
                {stage === 'publishing' && 'Publishing...'}
              </p>
            </div>
          </div>
          <button 
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {/* ═══ CAPTURE STAGE ═══ */}
          {stage === 'capture' && (
            <div className="space-y-4">
              {/* Camera View */}
              {showCamera ? (
                <div className="relative aspect-[4/3] bg-black rounded-xl overflow-hidden">
                  <video 
                    ref={videoRef} 
                    autoPlay 
                    playsInline 
                    muted
                    className="w-full h-full object-cover"
                  />
                  <canvas ref={canvasRef} className="hidden" />
                  
                  {/* Capture Controls */}
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-3">
                    <button
                      onClick={stopCamera}
                      className="w-11 h-11 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                    <button
                      onClick={capturePhoto}
                      className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
                      data-testid="capture-photo-btn"
                    >
                      <div className="w-11 h-11 border-[3px] border-[#FF6B35] rounded-full" />
                    </button>
                  </div>
                </div>
              ) : imagePreview ? (
                /* Image Preview */
                <div className="relative aspect-[4/3] bg-gray-100 rounded-xl overflow-hidden">
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  <button
                    onClick={() => { setImagePreview(null); setImageFile(null); }}
                    className="absolute top-3 right-3 p-2 bg-black/50 backdrop-blur-sm rounded-full text-white hover:bg-black/70 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                /* Upload Options - Clean grid */
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={startCamera}
                    className="flex flex-col items-center justify-center gap-2 py-8 bg-gray-50 hover:bg-[#FFF9F0] rounded-xl border-2 border-dashed border-gray-200 hover:border-[#FF6B35]/40 transition-all group"
                    data-testid="open-camera-btn"
                  >
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
                      <Camera className="w-5 h-5 text-gray-400 group-hover:text-[#FF6B35] transition-colors" />
                    </div>
                    <span className="text-sm font-medium text-gray-600 group-hover:text-[#FF6B35]">Take Photo</span>
                  </button>
                  
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex flex-col items-center justify-center gap-2 py-8 bg-gray-50 hover:bg-[#FFF9F0] rounded-xl border-2 border-dashed border-gray-200 hover:border-[#FF6B35]/40 transition-all group"
                    data-testid="upload-gallery-btn"
                  >
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
                      <ImageIcon className="w-5 h-5 text-gray-400 group-hover:text-[#FF6B35] transition-colors" />
                    </div>
                    <span className="text-sm font-medium text-gray-600 group-hover:text-[#FF6B35]">From Gallery</span>
                  </button>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    style={{ position: 'absolute', width: 0, height: 0, opacity: 0, overflow: 'hidden', pointerEvents: 'none' }}
                  />
                </div>
              )}

              {/* Venue Input (shown when image selected) */}
              {imagePreview && (
                <Input
                  placeholder="Where is this from? (optional)"
                  value={venueName}
                  onChange={(e) => setVenueName(e.target.value)}
                  className="bg-gray-50 border-gray-200 rounded-xl"
                />
              )}

              {/* Location indicator */}
              {locationString && (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <MapPin className="w-3 h-3" />
                  <span>{locationString}</span>
                </div>
              )}

              {/* Analyze Button */}
              {imagePreview && (
                <Button
                  onClick={handleAnalyze}
                  className="w-full bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white rounded-xl h-11 font-semibold gap-2 hover:opacity-90 transition-opacity"
                  data-testid="analyze-btn"
                >
                  <Sparkles className="w-4 h-4" />
                  AI Magic
                </Button>
              )}
            </div>
          )}

          {/* ═══ ANALYZING STAGE ═══ */}
          {stage === 'analyzing' && (
            <div className="py-12 text-center">
              <div className="relative w-16 h-16 mx-auto mb-4">
                <div className="absolute inset-0 bg-gradient-to-r from-[#F4C430] to-[#FF6B35] rounded-2xl animate-pulse" />
                <div className="absolute inset-1.5 bg-white rounded-xl flex items-center justify-center">
                  <Loader className="w-7 h-7 text-[#FF6B35] animate-spin" />
                </div>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Analyzing your dish...</h3>
              <p className="text-sm text-gray-500">Identifying ingredients & nutrition</p>
            </div>
          )}

          {/* ═══ PREVIEW STAGE ═══ */}
          {stage === 'preview' && cardData && (
            <div className="space-y-4">
              {/* Preview Card */}
              <div className="bg-gradient-to-br from-[#FFF9F0] to-[#FFF5F0] rounded-xl p-4 border border-[#FF6B35]/10">
                <div className="flex gap-3">
                  <img src={imagePreview} alt="Preview" className="w-16 h-16 object-cover rounded-lg" />
                  <div className="flex-1 min-w-0">
                    <Input
                      value={dishName}
                      onChange={(e) => setDishName(e.target.value)}
                      className="font-semibold text-gray-900 bg-transparent border-0 p-0 h-auto focus-visible:ring-0"
                      placeholder="Dish name"
                    />
                    <p className="text-xs text-gray-500 mt-0.5">{cuisine}</p>
                    
                    {/* Health Score & Calories */}
                    {(cardData.health_score || cardData.macros?.estimated_calories) && (
                      <div className="flex gap-1.5 mt-2">
                        {cardData.health_score && (
                          <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 text-[10px] px-1.5 py-0.5">
                            Health: {cardData.health_score}/10
                          </Badge>
                        )}
                        {cardData.macros?.estimated_calories && (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5">
                            ~{cardData.macros.estimated_calories} kcal
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Caption */}
              <Textarea
                placeholder="Add a caption..."
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                className="bg-gray-50 border-gray-200 rounded-xl resize-none text-sm"
                rows={2}
              />

              {/* Hashtags */}
              <div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {hashtags.map((tag, i) => (
                    <Badge 
                      key={i} 
                      variant="secondary" 
                      className="bg-[#FFF5F0] text-[#FF6B35] px-2 py-0.5 text-xs cursor-pointer hover:bg-[#FF6B35] hover:text-white transition-colors"
                      onClick={() => handleRemoveHashtag(tag)}
                    >
                      #{tag}
                      <X className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add hashtag"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddHashtag()}
                    className="bg-gray-50 border-gray-200 rounded-xl text-sm flex-1"
                  />
                  <Button onClick={handleAddHashtag} variant="outline" size="sm" className="rounded-xl px-3">
                    <Hash className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => handlePublish(true)}
                  variant="outline"
                  className="flex-1 rounded-xl h-10 gap-1.5 text-sm border-gray-200 hover:border-[#FF6B35]/30 hover:bg-[#FFF5F0]"
                  data-testid="save-foodlist-btn"
                >
                  <Bookmark className="w-4 h-4" />
                  Save to FoodList
                </Button>
                <Button
                  onClick={() => handlePublish(false)}
                  className="flex-1 bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white rounded-xl h-10 gap-1.5 text-sm font-semibold hover:opacity-90"
                  data-testid="post-feed-btn"
                >
                  Post to Feed
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* ═══ PUBLISHING STAGE ═══ */}
          {stage === 'publishing' && (
            <div className="py-12 text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-r from-[#F4C430] to-[#FF6B35] rounded-xl flex items-center justify-center">
                <Loader className="w-7 h-7 text-white animate-spin" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Publishing...</h3>
              <p className="text-sm text-gray-500">Almost there!</p>
            </div>
          )}
        </div>

        {/* Cancel link - subtle */}
        {(stage === 'capture' || stage === 'preview') && (
          <div className="px-5 pb-4 text-center">
            <button 
              onClick={handleClose}
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateFoodCardModal;
